import "./DealershipDetailsPage.css"
import React, {useEffect, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../redux/states/root.state";
import Header from "../../components/common/Header/Header";
import { AppDispatch } from "../../redux/store";
import { useParams } from "react-router-dom";
import { fetchDealership } from "../../redux/actions/dealership.actions";
import { ExternalLinkIcon, MinusIcon, PencilIcon, PlusIcon } from "../../components/common/Icons/Icons";
import DealershipUsersTable from "./components/DealershipUsersTable";
import AddStaffModal from "./components/AddStaffModal";
import RemoveStaffModal from "./components/RemoveStaffModal";
import UpdateDealershipModal from "../Dealerships/components/UpdateDealershipModal";
import NotFound from "../General/NotFound";
import usePermissionCheck from "../../hooks/usePermissionCheck";

const DealershipDetailsPage: React.FC = () => {
  // Data
  const {id} = useParams();
  const dispatch: AppDispatch = useDispatch();
  // Checked items
  const [editOpen, setEditOpen] = useState(false);
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [removeStaffOpen, setRemoveStaffOpen] = useState(false);

  // Data
  const {data: dealership, loading:isLoading, error} = useSelector((state: RootState) => state.dealership);

  const canEditDealership = usePermissionCheck(["update_dealership"]).canAccess;

  useEffect(() => {
    if (id){
      dispatch(fetchDealership(id));
    }
  }, [dispatch,id]);

  if (error && error.includes("Dealer not found")) {
    return <NotFound />;
  }

  // A link's address without the protocol, as shown in the card
  const displayUrl = (url: string) => {
    try {
      const {hostname, pathname} = new URL(url);
      return hostname + (pathname === '/' ? '' : pathname);
    } catch {
      return url;
    }
  };

  const externalLink = (url?: string) => url ? (
    <a className="lp-link" href={url} target="_blank" rel="noreferrer">{displayUrl(url)}<ExternalLinkIcon/></a>
  ) : <span className="lp-muted">None</span>;

  const location = [dealership?.city, dealership?.region?.label, dealership?.country?.label].filter(Boolean).join(', ');

  const toolbar = canEditDealership ? (
    <>
      <button type="button" className="btn btn-sm btn-primary lp-primary-action" onClick={() => setAddStaffOpen(true)}>
        <PlusIcon/> Add Staff
      </button>
      <button type="button" className="btn btn-sm dt-tool-btn lp-primary-action" onClick={() => setRemoveStaffOpen(true)}>
        <MinusIcon/> Remove Staff
      </button>
    </>
  ) : undefined;

  return (
    <div className="page lp-page">
      <AddStaffModal open={addStaffOpen} setOpen={setAddStaffOpen} object_id={id}/>
      <RemoveStaffModal open={removeStaffOpen} setOpen={setRemoveStaffOpen} object_id={id}/>
      <UpdateDealershipModal open={editOpen} setOpen={setEditOpen} object={dealership}/>
      <Header header="Dealership Details"/>
      <main className="lp-main">
        {isLoading ?
          <div className="lp-loading"><span className="spinner" role="progressbar" aria-label="Loading dealership"/></div>
          :
          <div className="lp-layout">
            <aside className="lp-side" aria-label="Dealership">
              <section className="lp-side-section">
                <div className="lp-side-head">
                  <h1 className="lp-side-name">{dealership?.name}</h1>
                  {canEditDealership &&
                    <button type="button" className="lp-side-btn" onClick={() => setEditOpen(true)} aria-label="Edit dealership"><PencilIcon/></button>}
                </div>
                <dl className="lp-facts">
                  <div><dt>Corporation</dt><dd>{dealership?.corporate?.name || <span className="lp-muted">None</span>}</dd></div>
                  <div><dt>Location</dt><dd>{location || <span className="lp-muted">None</span>}</dd></div>
                </dl>
              </section>

              <section className="lp-side-section">
                <h2 className="lp-side-title">Contact</h2>
                <dl className="lp-facts">
                  <div><dt>Phone</dt><dd>{dealership?.phone || <span className="lp-muted">None</span>}</dd></div>
                  <div><dt>Email</dt><dd>{dealership?.email || <span className="lp-muted">None</span>}</dd></div>
                  <div><dt>Website</dt><dd>{externalLink(dealership?.website_url)}</dd></div>
                  <div><dt>Supplies</dt><dd>{externalLink(dealership?.supplies_url)}</dd></div>
                </dl>
              </section>
            </aside>

            <DealershipUsersTable toolbar={toolbar}/>
          </div>}
      </main>
    </div>
  );
};

export default DealershipDetailsPage;
